import useSWR from "swr";
import {Utils} from "../common/utils";

const clientConfigs = Utils.getEAIClientConfigs();

export interface CompetencyInfo {
    ruuid: string
    competency: string
    subcompetency: string
    reason: string
    reported_verified: boolean
    reported_by: string
    reported_category: string
    reported_comment: string
}
export interface EnfluxQuestion {
    datasource: string
    school_id: string
    item_id: number
    question: string
    creator_name: string
    assessment_names: string[]
    course_names: string[]
    competency_info: CompetencyInfo[]
}

export interface StandardResult {
    total_count: number
}

export interface CategorizedQuestionsResponse {
    data: EnfluxQuestion[]
    result: StandardResult
}

export interface QuestionCategory {
    model_ruuid: string
    competency: string
    subcompetency: string
}

export interface QuestionCategoriesResponse {
    data: QuestionCategory[]
}

export interface EnfluxMLModel {
    ruuid: string
    name: string
    description: string
    groups: string[]
    enabled: boolean | null
}

export interface EnfluxMLModelsResponse {
    data: EnfluxMLModel[]
}

export const enfluxQuestionsKey = `${clientConfigs.endpoint}/schools/${clientConfigs.school}/categorized_questions/?`;

interface useEnfluxQuestionsProps {
    courseName: string
    assessmentName: string
    itemId: string
    itemCreator: string
    reason: string
    competency: string
    subCompetency: string
    pageSize: string
    pageNumber: string
    sortBy: string
    sortOrder: string
    verified: boolean
}
export function useEnfluxQuestions(
    {
        courseName,
        assessmentName,
        itemId,
        itemCreator,
        reason,
        competency,
        subCompetency,
        pageSize,
        pageNumber,
        sortBy,
        sortOrder,
        verified
    }: useEnfluxQuestionsProps) {
    const params: Record<string, string> = {
        verified: verified ? "true" : "false",
        page_size: pageSize,
        page: pageNumber,
        sort: sortBy,
        sort_order: sortOrder,
    };
    const filters = {};
    if (itemId) {
        filters["item_id"] = {operator: "eq", value: Number(itemId)};
    }
    if (itemCreator) {
        filters["creator_name"] = {operator: "contains", value: itemCreator};
    }
    if (courseName) {
        params["course"] = courseName;
    }
    if (assessmentName) {
        params["assessment"] = assessmentName;
    }
    if (competency) {
        params["competency"] = competency;
    }
    if (subCompetency) {
        params["subcompetency"] = subCompetency;
    }
    if (reason) {
        params["reason"] = reason;
    }

    params["filters"] = JSON.stringify(filters);
    const searchParams = new URLSearchParams(params);

    const {data, error, mutate} = useSWR<CategorizedQuestionsResponse, Error>(
        `${clientConfigs.endpoint}/schools/${clientConfigs.school}/categorized_questions/?${searchParams.toString()}`);

    return {
        data,
        error,
        mutate
    };
}

export function useQuestionsCategories(competency: string) {
    const {data, error, mutate} = useSWR<QuestionCategoriesResponse>(
        `${clientConfigs.endpoint}/schools/${clientConfigs.school}/categorized_questions/categories?competency=${competency}`,
        null,
        {
            revalidateOnFocus: false
        }
    );

    return {
        data,
        error,
        mutate
    };
}

export function useEnfluxModelsBySchool(schoolId: string) {
    const {data, error, mutate} = useSWR<EnfluxMLModelsResponse>(`${clientConfigs.endpoint}/schools/${schoolId}/categorizer_models/`);

    return {
        data,
        error,
        mutate
    };
}